import React, { useMemo, useCallback, useRef, useEffect, useState } from "react";
import { useDraggable } from "@dnd-kit/core";
import "./index.css";

const BASE_SIZE = 70;

const SHAPE_MAP = {
  1: "round",
  2: "square",
  3: "rect",
};

const OBJECT_MAP = {
  4: { cls: "bar", icon: "🍸", baseW: 120, baseH: 50 },
  5: { cls: "wall", icon: "🧱", baseW: 140, baseH: 20 },
  6: { cls: "plant", icon: "🌿", baseW: 40, baseH: 40 },
  7: { cls: "entrance", icon: "🚪", baseW: 60, baseH: 30 },
  8: { cls: "stage", icon: "🎤", baseW: 160, baseH: 80 },
  9: { cls: "dj", icon: "🎧", baseW: 60, baseH: 60 },
};

const getChairPositions = (shape, seatsCount) => {
  const chairs = [];
  if (shape === 1) {
    for (let i = 0; i < seatsCount; i++) {
      const angle = (360 / seatsCount) * i - 90;
      const rad = (angle * Math.PI) / 180;
      chairs.push({
        top: `${50 + 55 * Math.sin(rad)}%`,
        left: `${50 + 55 * Math.cos(rad)}%`,
        transform: "translate(-50%, -50%)",
      });
    }
  } else if (shape === 2) {
    const sides = [
      { pos: "top", style: (i, n) => ({ top: "-10px", left: `${((i + 1) / (n + 1)) * 100}%`, transform: "translateX(-50%)" }) },
      { pos: "bottom", style: (i, n) => ({ bottom: "-10px", left: `${((i + 1) / (n + 1)) * 100}%`, transform: "translateX(-50%)" }) },
      { pos: "left", style: (i, n) => ({ left: "-10px", top: `${((i + 1) / (n + 1)) * 100}%`, transform: "translateY(-50%)" }) },
      { pos: "right", style: (i, n) => ({ right: "-10px", top: `${((i + 1) / (n + 1)) * 100}%`, transform: "translateY(-50%)" }) },
    ];
    let remaining = seatsCount;
    let sideIdx = 0;
    while (remaining > 0 && sideIdx < sides.length) {
      const perSide = Math.ceil(remaining / (sides.length - sideIdx));
      for (let i = 0; i < perSide && remaining > 0; i++) {
        chairs.push(sides[sideIdx].style(i, perSide));
        remaining--;
      }
      sideIdx++;
    }
  } else if (shape === 3) {
    const topCount = Math.ceil(seatsCount / 2);
    const bottomCount = seatsCount - topCount;
    for (let i = 0; i < topCount; i++) {
      chairs.push({ top: "-10px", left: `${((i + 1) / (topCount + 1)) * 100}%`, transform: "translateX(-50%)" });
    }
    for (let i = 0; i < bottomCount; i++) {
      chairs.push({ bottom: "-10px", left: `${((i + 1) / (bottomCount + 1)) * 100}%`, transform: "translateX(-50%)" });
    }
  }
  return chairs;
};

export function Draggable(props) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: props.id,
  });

  const wheelRef = useRef(null);
  const [isSelected, setIsSelected] = useState(false);

  const isObject = props.shape >= 4;
  const objectDef = OBJECT_MAP[props.shape];

  const shapeClass = SHAPE_MAP[props.shape] || "round";
  const scale = props.scale || 1.0;
  const tableScale = scale * (props.scaleFactor || 1);

  let width, height;
  if (isObject && objectDef) {
    width = objectDef.baseW * tableScale;
    height = objectDef.baseH * tableScale;
  } else {
    const baseWidth = shapeClass === "rect" ? BASE_SIZE * 2.2 : BASE_SIZE;
    width = baseWidth * tableScale;
    height = BASE_SIZE * tableScale;
  }

  const statusClass = !isObject
    ? props.busy ? "busy" : props.nextReservation ? "reserved" : ""
    : "";

  const chairs = useMemo(
    () => (isObject ? [] : getChairPositions(props.shape, props.seatsCount)),
    [props.shape, props.seatsCount, isObject]
  );

  const style = {
    position: "absolute",
    transform: transform
      ? `translate3d(${props.position.x + transform.x}px, ${
          props.position.y + transform.y
        }px, 0)`
      : `translate3d(${props.position.x}px, ${props.position.y}px, 0)`,
    cursor: "move",
    width: `${width}px`,
    height: `${height}px`,
  };

  // Deselect when clicking outside
  useEffect(() => {
    if (!isSelected) return;
    const handleClickOutside = (e) => {
      if (wheelRef.current && !wheelRef.current.contains(e.target)) {
        setIsSelected(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSelected]);

  const handleWheel = useCallback(
    (e) => {
      if (!isSelected) return;
      e.preventDefault();
      e.stopPropagation();
      const delta = e.deltaY > 0 ? -1 : 1;

      if (e.shiftKey) {
        const step = 15;
        const newDirection = ((props.direction || 0) + delta * step + 360) % 360;
        if (props.onRotateChange) {
          props.onRotateChange(props.id, newDirection);
        }
      } else {
        const step = 0.1;
        const currentScale = props.scale || 1.0;
        const newScale = Math.round(Math.min(2.0, Math.max(0.5, currentScale + delta * step)) * 10) / 10;
        if (props.onScaleChange) {
          props.onScaleChange(props.id, newScale);
        }
      }
    },
    [props, isSelected]
  );

  useEffect(() => {
    const node = wheelRef.current;
    if (!node) return;
    node.addEventListener("wheel", handleWheel, { passive: false });
    return () => node.removeEventListener("wheel", handleWheel);
  }, [handleWheel]);

  // Render decorative object (shape >= 4)
  if (isObject && objectDef) {
    return (
      <div
        ref={(node) => {
          setNodeRef(node);
          wheelRef.current = node;
        }}
        style={style}
        {...attributes}
        {...listeners}
        className={`reservations-schema-draggable-container${isSelected ? " selected" : ""}`}
        onClick={(e) => {
          if (e.detail === 2 && props.onDoubleClick) {
            props.onDoubleClick(props.id);
          } else if (e.detail === 1) {
            setIsSelected(true);
          }
        }}
      >
        <div
          className={`reservations-schema-object-surface ${objectDef.cls}`}
          style={{
            transform: `rotate(${props.direction}deg)`,
            transition: "transform 0.3s ease",
          }}
        >
          {objectDef.icon}
        </div>
        <span className="reservations-schema-object-label">
          {props.tableName}
        </span>
        <div className="reservations-schema-table-tooltip">
          Scroll: размер | Shift+Scroll: завъртане
        </div>
      </div>
    );
  }

  // Render table (shape 1-3)
  return (
    <div
      ref={(node) => {
        setNodeRef(node);
        wheelRef.current = node;
      }}
      style={style}
      {...attributes}
      {...listeners}
      className={`reservations-schema-draggable-container ${statusClass}${isSelected ? " selected" : ""}`}
      onClick={(e) => {
        if (e.detail === 2) {
          props.onDoubleClick(props.id);
        } else if (e.detail === 1) {
          setIsSelected(true);
        }
      }}
    >
      <div
        className={`reservations-schema-table-surface ${shapeClass} ${statusClass}`}
        style={{
          transform: `rotate(${props.direction}deg)`,
          transition: "transform 0.3s ease",
        }}
      >
        {chairs.map((pos, idx) => (
          <div
            key={idx}
            className="reservations-schema-chair"
            style={pos}
          />
        ))}
      </div>
      <div className="reservations-schema-table-label">
        <span className="reservations-schema-table-name">
          {props.tableName}
        </span>
        <span className="reservations-schema-table-seats">
          {props.seatsCount} м.
        </span>
      </div>
      <div className="reservations-schema-table-tooltip">
        Scroll: размер | Shift+Scroll: завъртане
      </div>
    </div>
  );
}
